import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Shield, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
    <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 space-y-4 text-white">
      <div className="flex items-center gap-2"><Shield className="w-7 h-7 text-blue-500" /><span className="font-bold text-lg">NotaryOS</span></div>
      {children}
    </div>
  </div>
);

export function PayChargePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const order = params.get('token');
    const run = async () => {
      try {
        if (params.get('ok') && order) setC((await axios.post(`${API}/paypal/pay/${token}/capture`, { order_id: order })).data);
        else setC((await axios.get(`${API}/paypal/pay/${token}`)).data);
      } catch (e) { setErr(e.response?.data?.detail || 'No se pudo cargar el cobro'); }
    };
    run();
  }, [token, params]);

  const pay = async () => {
    setBusy(true);
    try { window.location.href = (await axios.post(`${API}/paypal/pay/${token}/start`)).data.approve_url; }
    catch (e) { setErr(e.response?.data?.detail || 'No se pudo iniciar el pago'); setBusy(false); }
  };

  if (err) return <Shell><p data-testid="pay-error" className="text-red-300 text-sm">{err}</p></Shell>;
  if (!c) return <Shell><Loader2 className="w-6 h-6 animate-spin" /></Shell>;
  return (
    <Shell>
      <p className="text-sm text-slate-400">Cobro de {c.issuer} para {c.client_name}</p>
      <h1 data-testid="pay-concept" className="text-2xl font-bold">{c.concept}</h1>
      <p data-testid="pay-amount" className="text-3xl font-bold">USD {c.amount_usd.toFixed(2)}</p>
      {c.amount_clp_ref ? <p className="text-sm text-slate-400">Referencia: ${c.amount_clp_ref.toLocaleString('es-CL')} CLP</p> : null}
      {c.status === 'pagado' ? (
        <p data-testid="pay-done" className="flex items-center gap-2 text-emerald-300 font-semibold"><CheckCircle2 className="w-5 h-5" />Pago recibido. ¡Gracias!</p>
      ) : (
        <button data-testid="pay-paypal-btn" onClick={pay} disabled={busy} className="w-full bg-[#ffc439] hover:bg-[#f2b92f] text-slate-900 font-bold rounded-lg py-3 flex justify-center">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Pagar con PayPal o tarjeta'}
        </button>
      )}
    </Shell>
  );
}

export function PayPalSubscriptionReturnPage() {
  const { api } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [msg, setMsg] = useState('Confirmando tu suscripción con PayPal...');
  useEffect(() => {
    const id = params.get('subscription_id');
    if (!id) { setMsg('No recibimos la suscripción de PayPal.'); return; }
    api.post('/paypal/subscription/confirm', { subscription_id: id })
      .then((r) => setMsg(r.data.active ? '¡Listo! Tu plan Premium está activo.' : `PayPal aún procesa tu suscripción (estado: ${r.data.status}). Se activará en unos minutos.`))
      .catch((e) => setMsg(e.response?.data?.detail || 'No se pudo confirmar la suscripción'));
  }, [api, params]);
  return (
    <Shell>
      <p data-testid="paypal-sub-message" className="text-sm">{msg}</p>
      <button data-testid="paypal-sub-dashboard-btn" onClick={() => navigate('/dashboard')} className="w-full btn-primary py-2.5">Ir a mi panel</button>
    </Shell>
  );
}
